import * as React from "react"

/**
 * SSR-safe matchMedia hook.
 *
 * Returns `false` on the server and on the first client render (so server HTML
 * and the hydration pass agree — no mismatch), then resolves to the real match
 * inside an effect after mount, and tracks it live via a `change` listener.
 *
 * Used by the hero to mount the static (frozen) pillar scenes only on small
 * viewports while desktop keeps the live animated scenes. Keep the desktop path
 * the SSR default so above-the-fold desktop paints the real scene immediately;
 * mobile swaps to the static one tick after hydration.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = React.useState(false)

  React.useEffect(() => {
    const mql = window.matchMedia(query)
    const onChange = () => setMatches(mql.matches)
    onChange()
    mql.addEventListener("change", onChange)
    return () => mql.removeEventListener("change", onChange)
  }, [query])

  return matches
}
